import { get } from 'lodash';

import { ValidationResultItem } from '../typings/common';
import {
    ApiMobileContentInfoResponse,
    AppFromApiData,
    AppInfo,
    AppsEntry,
    AppSettings,
    JavaMobileAppInfo,
    JavaMobileContent,
    MobileSysTypes,
    PropagationMode
} from '../typings/mobile-apps';
import { AppTracker, JavaAppTrackersTemplates, JavaAppTrackerTemplate } from '../typings/trackingParametersEditor';

const attrsMap = {
    price: 'PRICE',
    rating: 'RATING',
    votes: 'RATING_VOTES',
    icon: 'ICON'
};

const emptyTracker = {
    trackingSystem: 'EMPTY',
    trackingSystemName: 'EMPTY',
    editable: false,
    parameters: [],
    urlTemplate: '',
    baseUrlTemplate: '',
    urlTemplateIos: '',
    urlTemplateAndroid: ''
} as JavaAppTrackerTemplate;

export function mapApiFormatToAppsData(javaAppsData: Array<JavaMobileAppInfo>): AppsEntry[] {
    return javaAppsData.map(function(appData: JavaMobileAppInfo): AppsEntry {
        const trackName = get(appData, 'trackers[0].trackingSystemName');
        const trackSys = get(appData, 'trackers[0].trackingSystem');
        const trackIds = get(appData, 'trackers[0].trackerId');
        const trackUrls = get(appData, 'trackers[0].url');

        return {
            id: appData.id,
            appName: appData.name,
            storeAppName: appData.mobileContent.name,
            storeName: appData.store,
            storeAppId: appData.storeHref,
            iconUrl: appData.mobileContent.icon_url,
            trackName: trackName,
            trackSys: trackSys,
            trackIds: [trackIds],
            trackUrls: [trackUrls],
            files: []
        };
    });
}
export function mapApiFormatToAppData(appData: JavaMobileAppInfo): AppFromApiData {
    const attrsRevMap = Object.keys(attrsMap).reduce((result, nextKey) => {
        result[attrsMap[nextKey]] = nextKey;
        return result;
    }, {});

    const showInAd = appData.displayedAttributes.reduce((result, next) => {
        result[attrsRevMap[next]] = true;
        return result;
    }, {});

    const tracker = appData.trackers.length ? appData.trackers[0] : emptyTracker;

    return {
        appInfo: mapApiFormatToAppInfo(appData.mobileContent, appData.storeHref),
        appSettings: {
            id: appData.id,
            storeHref: appData.storeHref,
            name: appData.name,
            showInAd: showInAd,
            domain: appData.domain,
            propagationMode: PropagationMode.APPLY_TO_BANNERS_WITH_SAME_TRACKING_URL_AND_REPLACE_CHANGED
        },
        tracker: tracker
    };
}

export function mapApiFormatToAppInfo(
    restAppInfo: ApiMobileContentInfoResponse | JavaMobileContent,
    link: string,
    errors: ValidationResultItem[] = []
): AppInfo {
    const actions = restAppInfo.available_actions || [];
    const prices = restAppInfo.prices || {};

    let price = 0;
    let currency = '';

    // Если есть допустимые действия (скачать, купить и т.п.)
    // берем первое и берем цену из объекта цен
    if (Object.keys(prices).length && actions.length) {
        price = Number(prices[actions[0]].price);
        currency = prices[actions[0]].price_currency;
    }

    return {
        name: restAppInfo.name || '',
        iconUrl: restAppInfo.icon_url || '',
        rating: restAppInfo.rating || 0,
        votes: restAppInfo.rating_votes || 0,
        price: price,
        currency: currency,
        appUrl: link,
        osType: restAppInfo.os_type,
        errors: errors,
        publisherDomain: restAppInfo.publisherDomain
    };
}

export function mapAppSettingsToApiFormat(appSettings: AppSettings, tracker: AppTracker): {} {
    const displayedAttributes = [] as String[];
    let trackers = [] as AppTracker[];

    Object.keys(appSettings.showInAd).forEach(function(attr: string): void {
        if (appSettings.showInAd[attr]) {
            displayedAttributes.push(attrsMap[attr]);
        }
    });

    // если ссылка не задана, отправляем бэку пустой массив
    if (tracker.trackingSystem !== emptyTracker.trackingSystem) {
        trackers = [
            {
                trackerId: tracker.trackingSystem === MobileSysTypes.other ? undefined : tracker.trackerId,
                trackingSystem: tracker.trackingSystem,
                url: tracker.url,
                userParams: (tracker.selectedUserParams || []).map(param => param.name)
            }
        ];
    }

    return {
        id: appSettings.id,
        storeHref: appSettings.storeHref,
        name: appSettings.name,
        displayedAttributes: displayedAttributes,
        domain: appSettings.canEditMobileAppDomain ? appSettings.domain : null,
        trackers: trackers,
        propagationMode: appSettings.propagationMode
    };
}

export function mapAppTrackersUrlTemplateList(trackersTemplates: JavaAppTrackersTemplates): JavaAppTrackersTemplates {
    // Меняем плейсхолдер в базовых урлах т.к. $ невалидный символ и будет экранироваться
    return trackersTemplates
        .map(template => {
            if (template.baseUrlTemplate) {
                template.baseUrlTemplate = template.baseUrlTemplate.replace('${tracker_id}', '{tracker_id}');
            }

            return template;
        })
        .concat(emptyTracker);
}
